import logger from "../logger";

export const MAX_CONCURRENT_REQUESTS = 6;
export const CACHE_TTL_MS = 30 * 60 * 1000;
export const MAX_CACHE_ENTRIES = 500;
const REQUEST_LOG_MAX = 100;
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 3000;
const HTTP_TOO_MANY_REQUESTS = 429;
const CANCELLATION_RESET_DELAY_MS = 100;
export const DEFAULT_LOG_COUNT = 10;
export const STREAMING_INTERVAL_MS = 500;

const responseCache = new Map();
const inFlightRequests = new Map();
const activeAbortControllers = new Set();
let activeRequests = 0;
const waitQueue = [];
let cancelled = false;
const requestLog = [];

function shortenUrl(url) {
  try {
    const u = new URL(url);
    return u.pathname.replace(/^\/staging-proxy|^\/local-proxy/, "") + u.search;
  } catch {
    return url;
  }
}

function addLogEntry(url, status, source, body) {
  const now = new Date();
  const time = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  requestLog.push({ time, url: shortenUrl(url), status, source, body });
  if (requestLog.length > REQUEST_LOG_MAX) {
    requestLog.shift();
  }
}

export function getRequestLog(count = DEFAULT_LOG_COUNT) {
  const entries = requestLog.slice(-count).reverse();
  return entries
    .map((e) => {
      const bodyStr = e.body !== undefined ? JSON.stringify(e.body) : "";
      return `[${e.time}] ${e.url} → ${e.status}${e.source ? ` (${e.source})` : ""}${bodyStr ? `\n  ${bodyStr}` : ""}`;
    })
    .join("\n");
}

function acquireSlot() {
  if (cancelled) {
    return Promise.reject(new Error("Cancelled"));
  }
  if (activeRequests < MAX_CONCURRENT_REQUESTS) {
    activeRequests++;
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    waitQueue.push({ resolve, reject });
  });
}

function releaseSlot() {
  if (waitQueue.length > 0) {
    const next = waitQueue.shift();
    next.resolve();
  } else {
    activeRequests--;
  }
}

function evictExpiredEntries() {
  const now = Date.now();
  for (const [key, entry] of responseCache) {
    if (now - entry.timestamp > CACHE_TTL_MS) {
      responseCache.delete(key);
    }
  }
}

function evictLRU() {
  if (responseCache.size <= MAX_CACHE_ENTRIES) return;

  let oldestKey = null;
  let oldestAccess = Infinity;
  for (const [key, entry] of responseCache) {
    if (entry.lastAccess < oldestAccess) {
      oldestAccess = entry.lastAccess;
      oldestKey = key;
    }
  }
  if (oldestKey) responseCache.delete(oldestKey);
}

function getCached(url) {
  const entry = responseCache.get(url);
  if (!entry) return null;

  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    responseCache.delete(url);
    return null;
  }

  entry.lastAccess = Date.now();
  return entry.data;
}

function setCached(url, data) {
  evictExpiredEntries();
  const now = Date.now();
  responseCache.set(url, { data, timestamp: now, lastAccess: now });
  evictLRU();
}

async function fetchWithRetry(url, options, attempt = 0) {
  const response = await fetch(url, options);

  if (response.status === HTTP_TOO_MANY_REQUESTS && attempt < MAX_RETRIES) {
    const retryAfterHeader = response.headers.get("Retry-After");
    const delay = retryAfterHeader
      ? parseInt(retryAfterHeader, 10) * 1000
      : BASE_RETRY_DELAY_MS * Math.pow(2, attempt);

    logger.warn(
      `Rate limited (${HTTP_TOO_MANY_REQUESTS}) on ${url}. Retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms`
    );

    await new Promise((resolve) => setTimeout(resolve, delay));
    return fetchWithRetry(url, options, attempt + 1);
  }

  return response;
}

export async function managedFetch(url, options) {
  if (cancelled) {
    addLogEntry(url, "CANCELLED", null, undefined);
    throw new Error("Cancelled");
  }

  const cached = getCached(url);
  if (cached) {
    addLogEntry(url, `${cached.status} ${cached.statusText}`, "cache", cached.body);
    return cached;
  }

  if (inFlightRequests.has(url)) {
    addLogEntry(url, "DEDUP", "shared", undefined);
    return inFlightRequests.get(url);
  }

  const abortController = new AbortController();
  activeAbortControllers.add(abortController);

  const request = (async () => {
    await acquireSlot();
    try {
      const fetchOptions = { ...options, signal: abortController.signal };
      const response = await fetchWithRetry(url, fetchOptions);
      const data = {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        body: await response.json(),
        headers: response.headers,
      };

      addLogEntry(url, `${data.status} ${data.statusText}`, "fetch", data.body);

      if (data.ok) {
        setCached(url, data);
      }

      return data;
    } catch (err) {
      addLogEntry(url, `ERROR: ${err.message}`, "fetch", undefined);
      throw err;
    } finally {
      activeAbortControllers.delete(abortController);
      releaseSlot();
      inFlightRequests.delete(url);
    }
  })();

  inFlightRequests.set(url, request);
  return request;
}

export function getQueueStats() {
  return {
    active: activeRequests,
    queued: waitQueue.length,
    inFlight: inFlightRequests.size,
    cached: responseCache.size,
  };
}

export function cancelAllRequests() {
  cancelled = true;

  const abortedCount = activeAbortControllers.size;
  for (const controller of activeAbortControllers) {
    try {
      controller.abort();
    } catch (e) {
      logger.warn("Error aborting request: %s", e.message);
    }
  }
  activeAbortControllers.clear();

  const pending = waitQueue.splice(0);
  for (const entry of pending) {
    try {
      entry.reject(new Error("Cancelled"));
    } catch (e) {
      logger.warn("Error rejecting queued request: %s", e.message);
    }
  }

  inFlightRequests.clear();
  responseCache.clear();
  activeRequests = 0;

  logger.info("All requests cancelled — %d aborted, %d dequeued", abortedCount, pending.length);

  setTimeout(() => {
    cancelled = false;
  }, CANCELLATION_RESET_DELAY_MS);
}

export function clearRequestCache() {
  responseCache.clear();
  inFlightRequests.clear();
  activeRequests = 0;
  waitQueue.length = 0;
  logger.info("Request cache cleared");
}
